import { Drawer } from '@mui/material';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { Stack } from '@mui/system';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { useGetTransactionsQuery } from '@/api/v1';
import { ActivitySearchBar } from '@/components/organisms/ActivitySearchBar';
import { MainScreen } from '@/components/organisms/MainScreen';
import { TransactionsTable } from '@/components/organisms/TransactionsTable';
import { useCustomer } from '@/hooks/useCustomer';
import { useTranslator } from '@/hooks/useTranslator';
import { useTypedSearchParams } from '@/hooks/useTypedSearchParams';
import { CustomerBalanceSection } from '@/Screens/Dashboard/Customers/CustomerBalanceSection';
import { NoCustomerActivityResultsView } from '@/Screens/Dashboard/Customers/NoCustomerActivityResultsView';
import { NoCustomerActivityView } from '@/Screens/Dashboard/Customers/NoCustomerActivityView';
import { authSelector } from '@/StateManagement/AuthReducer';
import { BeneficiaryType } from '@/types/customer';
import { VerificationResult } from '@/types/customerKYXVerification';
import { GetTransactionsFilters } from '@/types/getTransactionsFilters';
import { CUSTOMER_ACTIVITY_COLUMNS } from '@/types/transactionColumn';
import { CUSTOMER_ACTIVITY_FILTERS } from '@/types/transactionFilterField';
import { countTransactionFilters } from '@/utility/countTransactionFilters';

export const OrgDetails: React.FC = (): React.ReactElement => {
  const { transactionId = '' } = useParams<{
    readonly transactionId: string;
  }>();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const tr = useTranslator();
  const customer = useCustomer();
  const { user } = useSelector(authSelector);
  const [filters, setFilters] = useTypedSearchParams<GetTransactionsFilters>(
    GetTransactionsFilters.initial(),
  );

  const beneficiaryId = customer.beneficiaryId;
  const orgName =
    customer.business?.name ||
    customer.businessInfo?.name ||
    user?.businessName ||
    customer.firstName ||
    '';

  const onRowClick = React.useCallback(
    (txId: string): void => {
      navigate({
        pathname: `activity/${txId}`,
        search: search.toString(),
      });
    },
    [navigate, search],
  );

  const closeTransactionDetails = React.useCallback((): void => {
    navigate({
      pathname: '.',
      search: search.toString(),
    });
  }, [navigate, search]);

  const handleFiltersChange = React.useCallback(
    (newFilters: GetTransactionsFilters): void => {
      const { walletCurrency } = filters as GetTransactionsFilters & {
        readonly walletCurrency?: string;
      };
      setFilters({
        ...newFilters,
        ...(walletCurrency ? { walletCurrency } : {}),
      } as GetTransactionsFilters);
    },
    [filters, setFilters],
  );

  const filtersCount = React.useMemo((): number => countTransactionFilters(filters), [filters]);

  const queryFilters = React.useMemo(
    (): Partial<GetTransactionsFilters> => ({
      ...filters,
      beneficiaryId: beneficiaryId ?? undefined,
    }),
    [filters, beneficiaryId],
  );

  const { data: transactionsPage } = useGetTransactionsQuery(queryFilters, {
    skip: !beneficiaryId,
  });

  const hasNoTransactions = !transactionsPage || transactionsPage.content.length === 0;
  const hasNoContent = hasNoTransactions && filtersCount === 0;

  const title = tr('ACCOUNTS');

  const breadcrumbs: React.ReactElement = (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        component={Link}
        to="/accounts"
        variant="h6"
        color="text.secondary"
        sx={styles.title}>
        {title}
      </Typography>
      <Typography variant="h6" color="text.secondary">
        {'>'}
      </Typography>
      {orgName && <Typography variant="h6">{orgName}</Typography>}
    </Stack>
  );

  return (
    <MainScreen title={breadcrumbs}>
      <Stack gap={4} sx={styles.container}>
        {beneficiaryId && (
          <CustomerBalanceSection
            beneficiaryId={beneficiaryId}
            beneficiaryType={BeneficiaryType.business}
            beneficiaryName={orgName}
          />
        )}

        <Paper elevation={3} sx={styles.activityPaper}>
          <Stack gap={4}>
            <Typography variant="h6">{tr('ACTIVITY')}</Typography>
            {beneficiaryId ? (
              <>
                {!hasNoContent && (
                  <ActivitySearchBar
                    filtersCount={filtersCount}
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    beneficiaryId={beneficiaryId}
                    visibleFilters={CUSTOMER_ACTIVITY_FILTERS}
                    isDownloadDisabled={hasNoTransactions}
                  />
                )}
                <TransactionsTable
                  beneficiaryId={beneficiaryId}
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  onRowClick={onRowClick}
                  noContentView={
                    <NoCustomerActivityView
                      verificationResult={VerificationResult.APPROVED}
                      beneficiaryId={beneficiaryId}
                    />
                  }
                  noResultsView={<NoCustomerActivityResultsView />}
                  visibleColumns={CUSTOMER_ACTIVITY_COLUMNS}
                />
              </>
            ) : (
              <NoCustomerActivityView
                verificationResult={VerificationResult.APPROVED}
                beneficiaryId={beneficiaryId}
              />
            )}
          </Stack>
        </Paper>
      </Stack>

      {transactionId ? (
        <Drawer open anchor="right" onClose={closeTransactionDetails}>
          <Outlet />
        </Drawer>
      ) : (
        <Outlet />
      )}
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  title: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  container: {
    pb: 2,
  },
  activityPaper: {
    px: 4,
    py: 3,
    borderRadius: 2,
  },
};
